/**
 * Editable Report Service
 * API calls for admin report management
 */

import apiClient from './AuthService';
import {
  EditableReport,
  EditableReportContent,
  AdminProject,
  AdminProjectDetail,
  GenerateReportResponse,
  RefineResponse,
} from '../Types/EditableReport.types';

const API_PREFIX = '/quiz/admin';

/**
 * Get all projects (for admin to select which to generate report for)
 */
export const getAllProjects = async (): Promise<AdminProject[]> => {
  try {
    const response = await apiClient.get<{ data: AdminProject[] }>(`${API_PREFIX}/projects`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

/**
 * Get the detail of a single project: general info + every group with its report status.
 */
export const getProjectDetail = async (projectId: string): Promise<AdminProjectDetail> => {
  try {
    const response = await apiClient.get<{ data: AdminProjectDetail }>(
      `${API_PREFIX}/projects/${projectId}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching project detail:', error);
    throw error;
  }
};

/**
 * Get all editable reports
 */
export const getAllReports = async (): Promise<EditableReport[]> => {
  try {
    const response = await apiClient.get<{ data: EditableReport[] }>(`${API_PREFIX}/reports`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

/**
 * Get a single report with its content
 */
export const getReport = async (reportId: string): Promise<EditableReport> => {
  try {
    const response = await apiClient.get<{ data: EditableReport }>(`${API_PREFIX}/reports/${reportId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching report:', error);
    throw error;
  }
};

/**
 * Create a new report for a project group (without running AI).
 * Returns the report in 'draft' status.
 */
export const generateReport = async (
  projectId: string,
  projectGroupId: string
): Promise<{ report: EditableReport }> => {
  try {
    const response = await apiClient.post<{ data: { report: EditableReport } }>(
      `${API_PREFIX}/reports/generate/${projectId}/${projectGroupId}`,
      {},
      { timeout: 30000 }
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error creating report:', error);
    throw error;
  }
};

/**
 * Dispatch AI generation job for an existing report.
 * Returns immediately with status 'generating'.
 * Poll getReport(id) until status changes to 'draft' or 'failed'.
 */
export const generateAi = async (reportId: string): Promise<EditableReport> => {
  try {
    const response = await apiClient.post<{ data: EditableReport }>(
      `${API_PREFIX}/reports/${reportId}/generate-ai`,
      {},
      { timeout: 30000 }
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error starting AI generation:', error);
    throw error;
  }
};

/**
 * Update report content
 */
export const updateReport = async (
  reportId: string,
  content: EditableReportContent,
  metadata?: Record<string, any>
): Promise<EditableReport> => {
  try {
    const response = await apiClient.put<{ data: EditableReport }>(
      `${API_PREFIX}/reports/${reportId}`,
      { content, metadata }
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error updating report:', error);
    throw error;
  }
};

/**
 * Publish a report (generates HTML)
 */
export const publishReport = async (reportId: string): Promise<{ report: EditableReport; htmlUrl: string }> => {
  try {
    const response = await apiClient.post<{ data: { report: EditableReport; htmlUrl: string } }>(
      `${API_PREFIX}/reports/${reportId}/publish`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error publishing report:', error);
    throw error;
  }
};

/**
 * Unpublish a report
 */
export const unpublishReport = async (reportId: string): Promise<EditableReport> => {
  try {
    const response = await apiClient.post<{ data: EditableReport }>(
      `${API_PREFIX}/reports/${reportId}/unpublish`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error unpublishing report:', error);
    throw error;
  }
};

/**
 * Refine report with AI
 * Note: This can take 1-2 minutes due to AI processing
 */
export const refineReport = async (reportId: string, correction: string): Promise<RefineResponse> => {
  try {
    const response = await apiClient.post<{ data: RefineResponse }>(
      `${API_PREFIX}/reports/${reportId}/refine`,
      { correction },
      { timeout: 180000 } // 3 minutes timeout for AI refinement
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error refining report:', error);
    throw error;
  }
};

/**
 * Delete a report
 */
export const deleteReport = async (reportId: string): Promise<void> => {
  try {
    await apiClient.delete(`${API_PREFIX}/reports/${reportId}`);
  } catch (error: any) {
    console.error('Error deleting report:', error);
    throw error;
  }
};

/**
 * Upload the final PDF for a specific project group of the report
 */
export const uploadFinalPdf = async (
  reportId: string,
  groupId: string,
  file: File
): Promise<EditableReport> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ data: EditableReport }>(
      `${API_PREFIX}/reports/${reportId}/groups/${groupId}/final-pdf`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error uploading final PDF:', error);
    throw error;
  }
};

/**
 * Delete the uploaded final PDF for a specific project group
 */
export const deleteFinalPdf = async (
  reportId: string,
  groupId: string
): Promise<EditableReport> => {
  try {
    const response = await apiClient.delete<{ data: EditableReport }>(
      `${API_PREFIX}/reports/${reportId}/groups/${groupId}/final-pdf`
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error deleting final PDF:', error);
    throw error;
  }
};

/**
 * Dispatch an AI generation for a single ProjectGroup of the project. Returns
 * once the job has been queued — the UI then polls getGroupMarkdown for
 * completion.
 */
export const generateAiForGroup = async (
  reportId: string,
  groupId: string
): Promise<void> => {
  try {
    await apiClient.post(
      `${API_PREFIX}/reports/${reportId}/groups/${groupId}/generate-ai`,
      {}
    );
  } catch (error: any) {
    console.error('Error dispatching per-group AI generation:', error);
    throw error;
  }
};

/**
 * Read the markdown of the per-group AI report (or null when not yet generated).
 * `generatedAt` lets the caller distinguish a regenerated markdown from the
 * previous one when polling.
 */
export const getGroupMarkdown = async (
  reportId: string,
  groupId: string
): Promise<{ markdown: string | null; generatedAt: string | null; available: boolean }> => {
  try {
    const response = await apiClient.get<{
      data: {
        projectGroupId: string;
        markdown: string | null;
        generatedAt: string | null;
        available: boolean;
      };
    }>(`${API_PREFIX}/reports/${reportId}/groups/${groupId}/markdown`);
    return {
      markdown: response.data.data.markdown,
      generatedAt: response.data.data.generatedAt,
      available: response.data.data.available,
    };
  } catch (error: any) {
    console.error('Error fetching per-group markdown:', error);
    throw error;
  }
};

/**
 * Fetch the report-renderer HTML scoped to a single ProjectGroup of the report.
 * Used by the admin editor to offer one HTML download per group.
 */
export const getGroupPreviewHtml = async (
  reportId: string,
  groupId: string
): Promise<string> => {
  try {
    const response = await apiClient.get<{ data: { html: string } }>(
      `${API_PREFIX}/reports/${reportId}/groups/${groupId}/preview-html`
    );
    return response.data.data.html;
  } catch (error: any) {
    console.error('Error fetching group preview HTML:', error);
    throw error;
  }
};

export default {
  getAllProjects,
  getProjectDetail,
  getAllReports,
  getReport,
  generateReport,
  updateReport,
  publishReport,
  unpublishReport,
  refineReport,
  deleteReport,
  uploadFinalPdf,
  deleteFinalPdf,
  getGroupPreviewHtml,
  generateAiForGroup,
  getGroupMarkdown,
};
